import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { auth } from 'src/utils/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';

type ProtectedRouteProps = {
  children: React.ReactNode;
  role?: string | string[];
};

const getCurrentUser = (): Promise<User | null> =>
  new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });

const checkClientIP = async () => {
  try {
    const fuser = await getCurrentUser();
    if (!fuser) return false;

    const token = await fuser.getIdToken();

    const response = await axios.get(
      'https://us-central1-lnp-orderhandling-app.cloudfunctions.net/checkTokenAuth',
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data.success;
  } catch (err) {
    return false;
  }
};

const loadingSpinnerStyle = {
  container: {
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9f9f9'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '6px solid #ddd',
    borderTop: '6px solid #333',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

const ProtectedRoute = ({ children, role }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const [ipAllowed, setIpAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    const run = async () => {
      const cached = sessionStorage.getItem('ip_allowed');

      if (cached !== null) {
        setIpAllowed(cached === 'true');
        return;
      }

      try {
        const allowed = await checkClientIP();
        sessionStorage.setItem('ip_allowed', String(allowed));
        setIpAllowed(allowed);
      } catch (e) {
        console.error('IP check failed', e);
        setIpAllowed(false);
      }
    };

    run();
  }, []);

  // ⏳ Wait for auth + IP check
  if (loading || ipAllowed === null) {
    return (
      <div style={loadingSpinnerStyle.container}>
        <div style={loadingSpinnerStyle.spinner}></div>
      </div>
    );
  }

  // 🔒 Not authenticated
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // 🔒 Role check
  if (role) {
    const roles = Array.isArray(role) ? role : [role];

    if (!roles.includes('billing_any') && !roles.includes(user.role)) {
      return <Navigate to="/403" replace />;
    }
  } else {
    return <Navigate to="/403" replace />;
  }

  // 🔒 IP check (UX only)
  if (!ipAllowed) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
